import { useEffect, useRef, useState, type DragEvent, type FormEvent } from 'react';
import {
  type DepartmentSummary,
  type ListSummary,
  type UserSummary, formatTaskKey,
  TASK_PRIORITY_LABEL,
  TASK_REPEAT_LABEL,
  TASK_STATUS_LABEL,
  type CompletionPolicy,
  type TaskAttachment,
  type TaskComment,
  type TaskStatus,
  type TaskSummary,
} from '@todo/shared';
import { taskApi } from '../api/task.api';
import { listApi } from '../api/list.api';
import { userApi } from '../api/user.api';
import { departmentApi } from '../api/department.api';
import { ApiError } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { AssigneePicker } from './AssigneePicker';
import { SidePanel } from './SidePanel';
import { StarButton } from './StarButton';
import { TaskEditForm } from './TaskEditForm';

/**
 * The task detail drawer, laid out to the approved design mock:
 * key chip in the drawer header, large title with the star, pill chips for
 * status/priority/due, a 2x2 meta card, dashed empty states, a drag-and-drop
 * file zone, the comment thread, and a sticky action footer.
 *
 * Opens with the row the list already has (instant), then refreshes from
 * GET /api/tasks/:id so what you read is current even if the list is stale.
 */

interface TaskDetailPanelProps {
  task: TaskSummary;
  onClose: () => void;
  /** The refreshed/updated row, so the list behind stays in sync. */
  onChanged: (task: TaskSummary) => void;
  /** Render in normal page flow instead of the usual dimmed overlay drawer -
   *  for a caller (the Notifications page's split view) that needs whatever
   *  sits beside this panel to stay fully visible and interactive. */
  inline?: boolean;
}

const STATUS_PILL: Record<TaskStatus, { chip: string; dot: string }> = {
  DRAFT: { chip: 'bg-slate-100 text-slate-600 ring-slate-200', dot: 'bg-slate-400' },
  PENDING: { chip: 'bg-sky-50 text-sky-700 ring-sky-200', dot: 'bg-sky-500' },
  ONGOING: { chip: 'bg-amber-50 text-amber-800 ring-amber-200', dot: 'bg-amber-500' },
  BLOCKED: { chip: 'bg-red-50 text-red-700 ring-red-200', dot: 'bg-red-500' },
  DONE: { chip: 'bg-emerald-50 text-emerald-700 ring-emerald-200', dot: 'bg-emerald-500' },
  CANCELLED: { chip: 'bg-slate-100 text-slate-400 ring-slate-200', dot: 'bg-slate-300' },
};

const PRIORITY_PILL: Record<string, string> = {
  HIGH: 'bg-red-50 text-red-700 ring-red-200',
  MEDIUM: 'bg-amber-50 text-amber-800 ring-amber-200',
  LOW: 'bg-slate-50 text-slate-600 ring-slate-200',
};

const POLICY_LABEL: Record<CompletionPolicy, string> = {
  ANY: 'Anyone assigned finishes it',
  ALL: 'Everyone assigned finishes their part',
  LEAD_ONLY: 'Only the lead finishes it',
};

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short', day: 'numeric', month: 'short',
    hour: 'numeric', minute: '2-digit',
  });
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1_048_576) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1_048_576).toFixed(1)} MB`;
}

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '').join('');
}

/** The mock's labelled section header with an optional action on the right. */
function SectionHead({ label, action }: { label: string; action?: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <h2 className="text-[13px] font-bold uppercase tracking-wider text-slate-500">{label}</h2>
      {action}
    </div>
  );
}

export function TaskDetailPanel({ task: initial, onClose, onChanged, inline }: TaskDetailPanelProps) {
  const { user } = useAuth();
  const [task, setTask] = useState<TaskSummary>(initial);
  const [starBusy, setStarBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [blocking, setBlocking] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [confirmCancel, setConfirmCancel] = useState(false);
  /** Assignee editing (creator only): a draft set until Save. */
  const [editingAssignees, setEditingAssignees] = useState(false);
  const [draftIds, setDraftIds] = useState<string[]>([]);
  const [draftLead, setDraftLead] = useState<string | undefined>(undefined);
  const [assignBusy, setAssignBusy] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [comments, setComments] = useState<TaskComment[] | null>(null);
  const [commentDraft, setCommentDraft] = useState('');
  const [commentBusy, setCommentBusy] = useState(false);
  const [attachments, setAttachments] = useState<TaskAttachment[] | null>(null);
  const [uploading, setUploading] = useState(false);
  const [collabError, setCollabError] = useState<string | null>(null);
  const [dropActive, setDropActive] = useState(false);
  /** Files picked or dropped, WAITING for the person to confirm the upload. */
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  /** Two-step delete: first click arms the row, second confirms. */
  const [editing, setEditing] = useState(false);
  /** My lists, each flagged with whether THIS task is in it. */
  const [myLists, setMyLists] = useState<ListSummary[] | null>(null);
  const [listBusyId, setListBusyId] = useState<string | null>(null);
  const [confirmDeleteFileId, setConfirmDeleteFileId] = useState<string | null>(null);
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  /** Which task the panel is SHOWING - navigating to a sub-task or the
   *  parent swaps this, and every per-task effect keys on it. */
  const [viewId, setViewId] = useState(initial.id);
  const [subtasks, setSubtasks] = useState<TaskSummary[] | null>(null);
  /** Who the NEXT quick-added sub-task goes to ('' = unassigned). */
  const [subAssignee, setSubAssignee] = useState('');
  /** Department for the NEXT quick-added sub-task ('' = none). Starts as the
   *  parent's department; picking another narrows the people select to that
   *  department's members. */
  const [subDept, setSubDept] = useState('');
  /** Org members for the quick-add select - fetched once, on first need. */
  const [people, setPeople] = useState<UserSummary[] | null>(null);
  /** Departments for the quick-add select - fetched once, on first need. */
  const [departments, setDepartments] = useState<DepartmentSummary[] | null>(null);
  const [subDraft, setSubDraft] = useState('');
  const [subBusy, setSubBusy] = useState(false);
  const [parentInfo, setParentInfo] = useState<TaskSummary | null>(null);

  // Refresh once per shown task; keep the stale row if the fetch fails.
  useEffect(() => {
    let cancelled = false;
    taskApi.byId(viewId)
      .then((fresh) => {
        if (cancelled) return;
        setTask(fresh);
        onChanged(fresh);
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewId]);

  // Thread, files and sub-tasks, loaded once per shown task. Attachment URLs
  // are short-lived SAS links, so navigating back refreshes them.
  useEffect(() => {
    let cancelled = false;
    setComments(null); setAttachments(null); setSubtasks(null); setParentInfo(null);
    void taskApi.comments(viewId)
      .then((c) => { if (!cancelled) setComments(c); })
      .catch(() => { if (!cancelled) setComments([]); });
    void listApi.all(viewId).then(setMyLists).catch(() => setMyLists([]));
    if (people === null) {
      void userApi.list({ pageSize: 100 })
        .then((res) => setPeople(res.data))
        .catch(() => setPeople([]));
    }
    if (departments === null) {
      void departmentApi.list()
        // Blank-id rows (hand-inserted) would render as "No department".
        .then((rows) => setDepartments(rows.filter((d) => d.id && d.id.trim() !== '')))
        .catch(() => setDepartments([]));
    }
    void taskApi.attachments(viewId)
      .then((a) => { if (!cancelled) setAttachments(a); })
      .catch(() => { if (!cancelled) setAttachments([]); });
    void taskApi.list({ parentId: viewId, pageSize: 50 })
      .then((res) => { if (!cancelled) setSubtasks(res.data); })
      .catch(() => { if (!cancelled) setSubtasks([]); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewId]);

  // The parent task, for the breadcrumb AND the assignment rule: someone
  // assigned to the parent may assign this sub-task.
  useEffect(() => {
    let cancelled = false;
    if (task.parentTaskId && task.id === viewId) {
      void taskApi.byId(task.parentTaskId)
        .then((p) => { if (!cancelled) setParentInfo(p); })
        .catch(() => undefined);
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.parentTaskId, viewId]);

  // The quick-add's department starts as the shown task's own; switching
  // tasks (or the refresh landing) re-seeds it and clears the person picked.
  useEffect(() => {
    setSubDept(task.departmentId ?? '');
    setSubAssignee('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewId, task.departmentId]);

  function navigateTo(next: TaskSummary) {
    setTask(next);
    setViewId(next.id);
  }

  async function addSubtask(event: FormEvent) {
    event.preventDefault();
    const title = subDraft.trim();
    if (!title || !subAssignee) return;
    setSubBusy(true);
    setCollabError(null);
    try {
      await taskApi.create({
        title,
        parentTaskId: task.id,
        // The chosen department (defaults to the parent's); '' sends none.
        ...(subDept ? { departmentId: subDept } : {}),
        // Exactly one, and never empty: the guard above and the disabled
        // Add button both stop an unassigned sub-task reaching the API.
        assigneeIds: [subAssignee],
      });
      setSubDraft('');
      const res = await taskApi.list({ parentId: viewId, pageSize: 50 });
      setSubtasks(res.data);
    } catch (err) {
      setCollabError(err instanceof ApiError ? err.message : 'Could not create the sub-task');
    } finally {
      setSubBusy(false);
    }
  }

  async function changeStatus(status: TaskStatus, blockedReason?: string) {
    setBusy(true);
    setActionError(null);
    try {
      const updated = await taskApi.setStatus(task.id, status, blockedReason);
      setTask(updated);
      onChanged(updated);
      setBlocking(false);
      setBlockReason('');
      setConfirmCancel(false);
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not update the status');
    } finally {
      setBusy(false);
    }
  }

  async function toggleStar() {
    setStarBusy(true);
    try {
      const updated = await taskApi.setImportant(task.id, !task.isImportant);
      setTask(updated);
      onChanged(updated);
    } catch {
      /* keep previous state */
    } finally {
      setStarBusy(false);
    }
  }

  function startAssigning() {
    setDraftIds(task.assignees.map((a) => a.userId));
    setDraftLead(task.assignees.find((a) => a.isLead)?.userId);
    setAssignError(null);
    setEditingAssignees(true);
  }

  async function saveAssignees() {
    setAssignBusy(true);
    setAssignError(null);
    try {
      const updated = await taskApi.setAssignees(task.id, draftIds, draftLead);
      setTask(updated);
      onChanged(updated);
      setEditingAssignees(false);
    } catch (err) {
      setAssignError(err instanceof ApiError ? err.message : 'Could not update the assignees');
    } finally {
      setAssignBusy(false);
    }
  }

  async function submitComment(event: FormEvent) {
    event.preventDefault();
    const body = commentDraft.trim();
    if (!body) return;
    setCommentBusy(true);
    setCollabError(null);
    try {
      setComments(await taskApi.addComment(task.id, body));
      setCommentDraft('');
    } catch (err) {
      setCollabError(err instanceof ApiError ? err.message : 'Could not post the comment');
    } finally {
      setCommentBusy(false);
    }
  }

  /** Picking or dropping only QUEUES the files - nothing leaves the browser
   *  until the person confirms. */
  function queueFiles(files: FileList | File[] | null) {
    const list = files ? Array.from(files) : [];
    if (list.length === 0) return;
    setCollabError(null);
    setPendingFiles((current) => [...current, ...list]);
    if (fileInput.current) fileInput.current.value = '';
  }

  async function uploadFiles(files: FileList | File[] | null) {
    const list = files ? Array.from(files) : [];
    if (list.length === 0) return;
    setPendingFiles([]);
    setUploading(true);
    setCollabError(null);
    try {
      // Sequential on purpose: each response is the fresh full list.
      let latest: TaskAttachment[] | null = null;
      for (const file of list) {
        latest = await taskApi.uploadAttachment(task.id, file);
      }
      if (latest) setAttachments(latest);
    } catch (err) {
      setCollabError(err instanceof ApiError ? err.message : 'Could not upload the file');
      void taskApi.attachments(task.id).then(setAttachments).catch(() => undefined);
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  /** Remove a wrong upload - the server answers with the remaining files. */
  async function deleteFile(file: TaskAttachment) {
    setDeletingFileId(file.id);
    setCollabError(null);
    try {
      setAttachments(await taskApi.deleteAttachment(task.id, file.id));
    } catch (err) {
      setCollabError(err instanceof ApiError ? err.message : 'Could not delete the file');
    } finally {
      setDeletingFileId(null);
      setConfirmDeleteFileId(null);
    }
  }

  /** Tick a list on or off for this task - labels, so toggling is safe. */
  async function toggleList(list: ListSummary) {
    setListBusyId(list.id);
    try {
      const next = list.containsTask
        ? await listApi.removeTask(list.id, task.id)
        : await listApi.addTask(list.id, task.id);
      setMyLists(next);
    } catch {
      /* leave the chips as they were */
    } finally {
      setListBusyId(null);
    }
  }

  function onDrop(event: DragEvent) {
    event.preventDefault();
    setDropActive(false);
    queueFiles(event.dataTransfer.files);
  }

  const isCreator = task.createdById === user?.id;
  const isAdmin =
    user?.role.systemKey === 'ADMIN' || user?.role.systemKey === 'SUPER_ADMIN';
  /** Mirrors the server: delegation is ADMIN / SUPER_ADMIN only - tasks and
   *  sub-tasks alike. Employees never assign work to anyone else; putting
   *  themselves on their own task is handled server-side as an only-self
   *  change, not as delegation. */
  const canAssign = isAdmin;
  /**
   * Editing the task's own fields (not assignment) needs to outrank the
   * CREATOR, not just hold an admin role - mirrors the server check in
   * task.repository.ts's updateTask. So an Admin still edits an Employee's
   * or a fellow Admin's task, but not a Super Admin's; an Employee only
   * ever edits their own.
   */
  const EDIT_RANK: Record<string, number> = { SUPER_ADMIN: 3, ADMIN: 2 };
  const myEditRank = EDIT_RANK[user?.role.systemKey ?? ''] ?? 1;
  const creatorEditRank = EDIT_RANK[task.createdByRoleKey ?? ''] ?? 1;
  const canEditTask = isCreator || (myEditRank >= 2 && myEditRank >= creatorEditRank);
  const me = task.assignees.find((a) => a.userId === user?.id);
  const isOpen = ['PENDING', 'ONGOING', 'BLOCKED'].includes(task.status);
  // DONE is reopenable; only CANCELLED closes the footer for good.
  // Any current assignee or the creator may act, and so may the task's
  // department's Admin / Super Admin - mirrors the server's oversight rule.
  const isDeptAdmin =
    isAdmin && !!task.departmentId && user?.department?.id === task.departmentId;
  const mayAct =
    (isOpen || task.status === 'DONE') &&
    (isCreator || me !== undefined || isDeptAdmin);
  const due = task.dueAt ? new Date(task.dueAt) : null;
  const dueToday = due && due.toDateString() === new Date().toDateString();

  /**
   * The quick-switch dropdown next to the status pill offers whatever the
   * footer already allows this actor to do from this status - a faster path
   * to the SAME transitions, not a new set of rules. DONE only reopens to
   * ONGOING, same as the "Reopen" button; CANCELLED is offered only to the
   * creator, same as the "Cancel task..." button. Everything the server
   * still enforces (LEAD_ONLY, open sub-tasks, terminal states) applies
   * exactly as it does today - a rejected change just surfaces as the same
   * actionError banner.
   */
  const statusOptions: TaskStatus[] = !mayAct
    ? []
    : task.status === 'DONE'
      ? ['DONE', 'ONGOING']
      : ['PENDING', 'ONGOING', 'BLOCKED', 'DONE', ...(isCreator ? ['CANCELLED' as TaskStatus] : [])];

  /** BLOCKED still needs a reason and CANCELLED still needs confirmation, so
   *  picking either just opens the footer's existing prompt instead of
   *  firing the change immediately - the dropdown snaps back to the current
   *  status until that prompt is completed, which is correct: nothing
   *  changed yet. */
  function selectStatus(next: TaskStatus) {
    if (next === task.status) return;
    setActionError(null);
    if (next === 'BLOCKED') { setBlocking(true); return; }
    if (next === 'CANCELLED') { setConfirmCancel(true); return; }
    void changeStatus(next);
  }

  return (
    <SidePanel open wide overlay={!inline} title={formatTaskKey(task.taskKey)} description="Task details" onClose={onClose}>
      {editing ? (
        <div className="flex-1 overflow-y-auto px-7 py-6">
          <TaskEditForm
            task={task}
            onCancel={() => setEditing(false)}
            onSaved={(t) => { setTask(t); onChanged(t); setEditing(false); }}
          />
        </div>
      ) : (
      <>
      <div className="flex-1 space-y-6 overflow-y-auto px-7 py-6">
        {/* Sub-task breadcrumb: one tap back to the parent. */}
        {task.parentTaskId && (
          <button type="button"
            onClick={() => {
              if (!task.parentTaskId) return;
              void taskApi.byId(task.parentTaskId).then(navigateTo).catch(() => undefined);
            }}
            className="-mb-3 text-[13px] font-semibold text-brand-600 hover:text-brand-700 hover:underline">
            ↑ Sub-task of {parentInfo ? formatTaskKey(parentInfo.taskKey) : 'parent task'}
          </button>
        )}

        {/* Title + star */}
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-[22px] font-bold leading-tight tracking-tight text-slate-900">
            {task.title}
          </h3>
          <div className="flex shrink-0 items-center gap-1.5">
            {/* Completed tasks are read-only - reopening (audited) is the
                one way to make them editable again. */}
            {canEditTask && task.status !== 'CANCELLED' && task.status !== 'DONE' && (
              <button type="button" onClick={() => setEditing(true)}
                className="rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50">
                Edit
              </button>
            )}
            <StarButton active={task.isImportant} busy={starBusy}
                        onToggle={() => void toggleStar()} />
          </div>
        </div>

        {/* Pill chips */}
        <div className="flex flex-wrap gap-2">
          {statusOptions.length > 1 ? (
            <span className="relative inline-flex items-center">
              <span className={`pointer-events-none absolute left-2.5 h-1.5 w-1.5 rounded-full ${STATUS_PILL[task.status].dot}`} />
              <select
                id="task-status-select"
                value={task.status}
                disabled={busy}
                onChange={(e) => selectStatus(e.target.value as TaskStatus)}
                aria-label="Change task status"
                className={`cursor-pointer appearance-none rounded-full py-1 pl-6 pr-6 text-[13px] font-semibold ring-1 outline-none transition disabled:cursor-default disabled:opacity-60 ${STATUS_PILL[task.status].chip}`}
              >
                {statusOptions.map((s) => (
                  <option key={s} value={s}>{TASK_STATUS_LABEL[s]}</option>
                ))}
              </select>
              <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"
                   className="pointer-events-none absolute right-1.5 h-3 w-3 opacity-60">
                <path d="M6 8l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          ) : (
            <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[13px] font-semibold ring-1 ${STATUS_PILL[task.status].chip}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${STATUS_PILL[task.status].dot}`} />
              {TASK_STATUS_LABEL[task.status]}
            </span>
          )}
          <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[13px] font-semibold ring-1 ${PRIORITY_PILL[task.priority]}`}>
            {TASK_PRIORITY_LABEL[task.priority]} priority
          </span>
          {due && (
            <span className="inline-flex items-center rounded-full bg-slate-50 px-2.5 py-1 text-[13px] font-semibold text-slate-600 ring-1 ring-slate-200">
              {dueToday
                ? `Due today · ${due.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`
                : `Due ${due.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}`}
            </span>
          )}
          {task.repeat !== 'NONE' && (
            <span className="inline-flex items-center rounded-full bg-violet-50 px-2.5 py-1 text-[13px] font-semibold text-violet-700 ring-1 ring-violet-200">
              {TASK_REPEAT_LABEL[task.repeat]}
            </span>
          )}
        </div>

        {task.details && (
          <p className="whitespace-pre-wrap text-base leading-relaxed text-slate-700">
            {task.details}
          </p>
        )}

        {/* Meta card: 2x2 grid + activity strip */}
        <section className="overflow-hidden rounded-xl border border-slate-200">
          <div className="grid grid-cols-2">
            <div className="border-b border-r border-slate-100 px-4 py-3.5">
              <p className="mb-1 text-[11px] font-bold uppercase tracking-wider text-slate-400">Due</p>
              <p className="text-[15px] font-semibold text-slate-900">{formatDateTime(task.dueAt)}</p>
            </div>
            <div className="border-b border-slate-100 px-4 py-3.5">
              <p className="mb-1 text-[11px] font-bold uppercase tracking-wider text-slate-400">Reminder</p>
              <p className="text-[15px] font-semibold text-slate-900">{formatDateTime(task.remindAt)}</p>
            </div>
            <div className="border-b border-r border-slate-100 px-4 py-3.5">
              <p className="mb-1 text-[11px] font-bold uppercase tracking-wider text-slate-400">Completes when</p>
              <p className="text-[15px] font-semibold text-slate-900">{POLICY_LABEL[task.completionPolicy]}</p>
            </div>
            <div className="border-b border-slate-100 px-4 py-3.5">
              <p className="mb-1 text-[11px] font-bold uppercase tracking-wider text-slate-400">Created by</p>
              <p className="flex items-center gap-2 text-[15px] font-semibold text-slate-900">
                <span className="grid h-[22px] w-[22px] place-items-center rounded-full bg-brand-600 text-[10px] font-bold text-white">
                  {initials(task.createdByName)}
                </span>
                {task.createdByName}
              </p>
            </div>
            <div className="border-b border-r border-slate-100 px-4 py-3.5">
              <p className="mb-1 text-[11px] font-bold uppercase tracking-wider text-slate-400">Department</p>
              <p className="text-[15px] font-semibold text-slate-900">{task.departmentName ?? '—'}</p>
            </div>
            <div className="border-b border-slate-100 px-4 py-3.5">
              <p className="mb-1 text-[11px] font-bold uppercase tracking-wider text-slate-400">Project</p>
              <p className="text-[15px] font-semibold text-slate-900">{task.projectName ?? '—'}</p>
            </div>
            <div className="border-b border-r border-slate-100 px-4 py-3.5">
              <p className="mb-1 text-[11px] font-bold uppercase tracking-wider text-slate-400">Progress</p>
              {task.subtaskCount === 0 ? (
                <p className="text-[15px] font-semibold text-slate-900">
                  {task.status === 'DONE' ? '100%' : 'No sub-tasks'}
                </p>
              ) : (
                <div>
                  <p className="text-[15px] font-semibold text-slate-900">
                    {Math.round((task.subtaskDoneCount / task.subtaskCount) * 100)}%
                    <span className="ml-1.5 text-xs font-normal text-slate-400">
                      {task.subtaskDoneCount}/{task.subtaskCount} sub-tasks
                    </span>
                  </p>
                  <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-emerald-400 transition-all"
                         style={{ width: `${(task.subtaskDoneCount / task.subtaskCount) * 100}%` }} />
                  </div>
                </div>
              )}
            </div>
            <div className="col-span-2 flex gap-6 bg-slate-50/70 px-4 py-3 text-[13px] text-slate-500">
              <span>Created <strong className="font-semibold text-slate-700">{formatDateTime(task.createdAt)}</strong></span>
              {task.reopenedAt && (
                <span className="text-amber-700">
                  Reopened <strong className="font-semibold">{formatDateTime(task.reopenedAt)}</strong>
                  {task.reopenedByName ? <> by <strong className="font-semibold">{task.reopenedByName}</strong></> : null}
                </span>
              )}
              <span>Last activity <strong className="font-semibold text-slate-700">{formatDateTime(task.lastActivityAt)}</strong></span>
            </div>
          </div>
        </section>

        {/* My lists - personal labels; only the signed-in user sees these. */}
        {myLists !== null && myLists.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="mr-1 text-[11px] font-bold uppercase tracking-wider text-slate-400">
              Lists
            </span>
            {myLists.map((list) => (
              <button
                key={list.id}
                type="button"
                disabled={listBusyId === list.id}
                onClick={() => void toggleList(list)}
                className={[
                  'rounded-full px-2.5 py-1 text-xs font-semibold ring-1 transition disabled:opacity-50',
                  list.containsTask
                    ? 'bg-brand-600 text-white ring-brand-600 hover:bg-brand-700'
                    : 'bg-white text-slate-500 ring-slate-300 hover:bg-slate-50',
                ].join(' ')}
              >
                {list.containsTask ? '✓ ' : '+ '}{list.name}
              </button>
            ))}
          </div>
        )}

        {/* Assignees */}
        <section className="space-y-2.5">
          <SectionHead
            label="Assignees"
            action={
              canAssign && !['DONE', 'CANCELLED'].includes(task.status) && !editingAssignees ? (
                <button type="button" onClick={startAssigning}
                  className="text-sm font-semibold text-brand-600 hover:text-brand-700 hover:underline">
                  {task.assignees.length === 0 ? '+ Assign people' : 'Edit assignees'}
                </button>
              ) : undefined
            }
          />

          {editingAssignees ? (
            <div className="space-y-3">
              {assignError && (
                <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 ring-1 ring-red-100">
                  {assignError}
                </p>
              )}
              <AssigneePicker
                selected={draftIds}
                leadId={draftLead}
                requireLead={task.completionPolicy === 'LEAD_ONLY'}
                onChange={(ids, lead) => { setDraftIds(ids); setDraftLead(lead); }}
                disabled={assignBusy}
              />
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setEditingAssignees(false)}
                  className="rounded-lg px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-100">
                  Cancel
                </button>
                <button type="button" disabled={assignBusy}
                  onClick={() => void saveAssignees()}
                  className="rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-brand-700 disabled:opacity-60">
                  {assignBusy ? 'Saving...' : 'Save assignees'}
                </button>
              </div>
            </div>
          ) : task.assignees.length === 0 ? (
            <div className="flex items-center gap-3 rounded-xl border border-dashed border-slate-300 bg-slate-50/70 px-4 py-3.5">
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-dashed border-slate-300 text-slate-400">+</span>
              <p className="text-sm leading-snug text-slate-600">
                <strong className="font-semibold text-slate-900">Nobody assigned yet.</strong><br />
                Only you can see this task until someone is added.
              </p>
            </div>
          ) : (
            <ul className="space-y-2">
              {task.assignees.map((a) => (
                <li key={a.userId}
                    className="flex items-start gap-3 rounded-xl bg-slate-50 px-3.5 py-2.5 ring-1 ring-slate-200">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-brand-100 text-xs font-semibold text-brand-700">
                    {initials(a.displayName)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-900">
                      {a.displayName}
                      {a.isLead && (
                        <span className="ml-1.5 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                          Lead
                        </span>
                      )}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Sub-tasks: only on top-level tasks - one level of nesting. */}
        {!task.parentTaskId && (
          <section className="space-y-2.5">
            <SectionHead
              label={`Sub-tasks${subtasks && subtasks.length > 0 ? ` (${subtasks.filter((t) => t.status === 'DONE').length}/${subtasks.length})` : ''}`}
            />

            {subtasks !== null && subtasks.length > 0 && (
              <ul className="space-y-1.5">
                {subtasks.map((sub) => (
                  <li key={sub.id}>
                    <button type="button" onClick={() => navigateTo(sub)}
                      className="flex w-full items-center gap-2.5 rounded-xl bg-slate-50 px-3.5 py-2.5 text-left ring-1 ring-slate-200 transition hover:ring-slate-300">
                      <span className={`h-2 w-2 shrink-0 rounded-full ${STATUS_PILL[sub.status].dot}`} />
                      <span className="min-w-0 flex-1">
                        <span className={[
                          'block truncate text-sm',
                          sub.status === 'DONE' ? 'text-slate-400 line-through' : 'text-slate-800',
                        ].join(' ')}>
                          {sub.title}
                        </span>
                        <span className="block text-[11px] text-slate-400">
                          {formatTaskKey(sub.taskKey)} · {TASK_STATUS_LABEL[sub.status]}
                          {sub.assignees.length > 0 && ` · ${sub.assignees.map((a) => a.displayName.split(' ')[0]).join(', ')}`}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {/* Anyone who can see the task can break it down. */}
            <form onSubmit={(e) => void addSubtask(e)} className="space-y-2">
              <input
                value={subDraft}
                onChange={(e) => setSubDraft(e.target.value)}
                maxLength={500}
                placeholder="Add a sub-task…"
                className="w-full rounded-xl border border-slate-300 px-3.5 py-2 text-sm outline-none transition placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
              />
              <div className="flex items-center gap-2">
                {/* Where the sub-task belongs - seeded from the parent, and
                    the people select narrows to this department's members. */}
                <select
                  value={subDept}
                  onChange={(e) => {
                    const next = e.target.value;
                    setSubDept(next);
                    // A person picked under the old department may not exist
                    // under the new one - drop them (keep "Me": self-assign
                    // is allowed regardless of department).
                    if (subAssignee && subAssignee !== user?.id) {
                      const picked = (people ?? []).find((p) => p.id === subAssignee);
                      if (next && picked?.departmentId !== next) setSubAssignee('');
                    }
                  }}
                  aria-label="Sub-task department"
                  className="min-w-0 flex-1 rounded-xl border border-slate-300 bg-white px-2.5 py-2 text-sm text-slate-700 outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
                >
                  <option value="">No department</option>
                  {(departments ?? []).map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
                {/* Assign on the spot. Yourself is always allowed; other people
                    only when you may assign sub-tasks here (admin, or assigned
                    to this parent) - mirrors the server rule. */}
                <select
                  value={subAssignee}
                  onChange={(e) => setSubAssignee(e.target.value)}
                  aria-label="Assign the sub-task to"
                  className="min-w-0 flex-1 rounded-xl border border-slate-300 bg-white px-2.5 py-2 text-sm text-slate-700 outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
                >
                  {/* A sub-task must have an assignee (the API refuses an
                      empty list), so this is the UNCHOSEN state, not a
                      selectable "nobody". Add stays disabled until it moves. */}
                  <option value="">Assign to...</option>
                  {user && <option value={user.id}>Me ({user.displayName.split(/\s+/)[0]})</option>}
                  {canAssign && (people ?? [])
                    .filter((p) => {
                      // Hierarchy mirror: never someone of HIGHER rank; peers
                      // at every level may pick each other (Admin to Admin,
                      // Super Admin to Super Admin, employee to employee).
                      if (p.id === user?.id) return false; // "Me" is above
                      const RANK: Record<string, number> = { SUPER_ADMIN: 3, ADMIN: 2 };
                      const mine = RANK[user?.role.systemKey ?? ''] ?? 1;
                      const theirs = RANK[p.roleSystemKey ?? ''] ?? 1;
                      return theirs <= mine;
                    })
                    // The chosen department's members only; no department
                    // chosen = the whole organisation, as before.
                    .filter((p) => !subDept || p.departmentId === subDept)
                    .map((p) => (
                      <option key={p.id} value={p.id}>{p.displayName}</option>
                    ))}
                </select>
                <button type="submit" disabled={subBusy || !subDraft.trim() || !subAssignee}
                  title={!subAssignee ? 'Pick who the sub-task is for' : undefined}
                  className="rounded-xl bg-brand-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-700 disabled:cursor-default disabled:bg-slate-100 disabled:text-slate-400">
                  {subBusy ? '...' : 'Add'}
                </button>
              </div>
            </form>
          </section>
        )}

        {/* Files */}
        <section className="space-y-2.5">
          <SectionHead
            label={`Files${attachments && attachments.length > 0 ? ` (${attachments.length})` : ''}`}
            action={
              <button type="button" disabled={uploading}
                onClick={() => fileInput.current?.click()}
                className="text-sm font-semibold text-brand-600 hover:text-brand-700 hover:underline disabled:opacity-50">
                {uploading ? 'Uploading...' : '↑ Upload files'}
              </button>
            }
          />
          <input ref={fileInput} type="file" multiple className="hidden"
            onChange={(e) => queueFiles(e.target.files)} />

          {collabError && (
            <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 ring-1 ring-red-100">
              {collabError}
            </p>
          )}

          {/* The confirmation step: nothing uploads until OK is given. */}
          {pendingFiles.length > 0 && (
            <div className="rounded-xl border border-brand-200 bg-brand-50/60 p-3.5">
              <p className="text-sm font-semibold text-slate-900">
                Upload {pendingFiles.length} {pendingFiles.length === 1 ? 'file' : 'files'} to {formatTaskKey(task.taskKey)}?
              </p>
              <ul className="mt-1.5 space-y-0.5">
                {pendingFiles.map((file, index) => (
                  <li key={`${file.name}-${index}`}
                      className="flex items-baseline gap-2 text-xs text-slate-600">
                    <span className="min-w-0 flex-1 truncate">{file.name}</span>
                    <span className="shrink-0 text-slate-400">{formatSize(file.size)}</span>
                    <button type="button" aria-label={`Remove ${file.name}`}
                      onClick={() => setPendingFiles((c) => c.filter((_, i) => i !== index))}
                      className="shrink-0 text-slate-400 hover:text-red-600">&times;</button>
                  </li>
                ))}
              </ul>
              <div className="mt-2.5 flex justify-end gap-2">
                <button type="button" onClick={() => setPendingFiles([])}
                  className="rounded-lg px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-100">
                  Cancel
                </button>
                <button type="button" disabled={uploading}
                  onClick={() => void uploadFiles(pendingFiles)}
                  className="rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-brand-700 disabled:opacity-60">
                  {uploading ? 'Uploading...' : 'OK, upload'}
                </button>
              </div>
            </div>
          )}

          {attachments !== null && attachments.length > 0 && (
            <ul className="space-y-1.5">
              {attachments.map((file) => (
                <li key={file.id}
                    className="flex items-center gap-2.5 rounded-xl bg-slate-50 px-3.5 py-2.5 ring-1 ring-slate-200">
                  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"
                       className="h-4 w-4 shrink-0 text-slate-400">
                    <path d="M11 3H6a1 1 0 00-1 1v12a1 1 0 001 1h8a1 1 0 001-1V7l-4-4zM11 3v4h4"
                          stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-slate-800">{file.fileName}</span>
                    <span className="block text-[11px] text-slate-400">
                      {formatSize(file.sizeBytes)} · {file.uploadedByName}
                    </span>
                  </span>
                  <a href={file.downloadUrl} download={file.fileName}
                     target="_blank" rel="noreferrer"
                     className="shrink-0 rounded-lg px-2 py-1 text-xs font-semibold text-brand-600 transition hover:bg-brand-50">
                    Download
                  </a>
                  {/* Uploader, creator, or admin may undo a wrong upload -
                      mirrors the server rule so the button only shows when
                      the request would succeed. */}
                  {(file.uploadedById === user?.id || isCreator || isAdmin) && (
                    confirmDeleteFileId === file.id ? (
                      <span className="flex shrink-0 items-center gap-1">
                        <button type="button"
                          disabled={deletingFileId === file.id}
                          onClick={() => void deleteFile(file)}
                          className="rounded-lg bg-red-600 px-2 py-1 text-xs font-semibold text-white transition hover:bg-red-700 disabled:opacity-50">
                          {deletingFileId === file.id ? 'Deleting...' : 'Delete'}
                        </button>
                        <button type="button"
                          onClick={() => setConfirmDeleteFileId(null)}
                          className="rounded-lg px-2 py-1 text-xs font-medium text-slate-500 transition hover:bg-slate-100">
                          Keep
                        </button>
                      </span>
                    ) : (
                      <button type="button"
                        onClick={() => setConfirmDeleteFileId(file.id)}
                        title="Delete this file"
                        aria-label={`Delete ${file.fileName}`}
                        className="shrink-0 rounded-lg px-2 py-1 text-xs font-semibold text-slate-400 transition hover:bg-red-50 hover:text-red-600">
                        &times;
                      </button>
                    )
                  )}
                </li>
              ))}
            </ul>
          )}

          {/* The drop zone doubles as the empty state and the browse control. */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDropActive(true); }}
            onDragLeave={() => setDropActive(false)}
            onDrop={onDrop}
            onClick={() => fileInput.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') fileInput.current?.click(); }}
            className={[
              'cursor-pointer rounded-xl border border-dashed px-4 py-4 text-center text-sm transition',
              dropActive
                ? 'border-brand-400 bg-brand-50/60 text-brand-700'
                : 'border-slate-300 bg-slate-50/70 text-slate-600 hover:border-slate-400',
            ].join(' ')}
          >
            {attachments === null
              ? 'Loading files...'
              : <>Drop files here, or <span className="font-semibold text-brand-600">browse</span>. Anyone on the task can add them.</>}
          </div>
        </section>

        {/* Comments */}
        <section className="space-y-3">
          <SectionHead
            label={`Comments${comments && comments.length > 0 ? ` (${comments.length})` : ''}`}
          />

          {comments === null ? (
            <div className="h-10 animate-pulse rounded-xl bg-slate-100" />
          ) : comments.length === 0 ? (
            <p className="text-sm text-slate-500">No comments yet — start the thread.</p>
          ) : (
            <ul className="space-y-2.5">
              {comments.map((comment) => (
                <li key={comment.id} className="flex items-start gap-3">
                  <span className="grid h-[30px] w-[30px] shrink-0 place-items-center rounded-full bg-brand-100 text-[11px] font-bold text-brand-700">
                    {initials(comment.authorName)}
                  </span>
                  <div className="min-w-0 rounded-xl bg-slate-50 px-3.5 py-2.5 ring-1 ring-slate-200">
                    <p className="text-[13px] text-slate-500">
                      <strong className="font-semibold text-slate-900">{comment.authorName}</strong>
                      {' · '}{formatDateTime(comment.createdAt)}
                    </p>
                    <p className="mt-0.5 whitespace-pre-wrap text-[15px] leading-normal text-slate-700">
                      {comment.body}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <form onSubmit={(e) => void submitComment(e)} className="flex items-end gap-2.5">
            <textarea
              value={commentDraft}
              onChange={(e) => setCommentDraft(e.target.value)}
              rows={2}
              maxLength={2000}
              placeholder="Write a comment…"
              className="min-h-[60px] min-w-0 flex-1 resize-y rounded-xl border border-slate-300 px-3.5 py-2.5 text-[15px] outline-none transition placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
            />
            <button type="submit" disabled={commentBusy || !commentDraft.trim()}
              className="rounded-xl bg-brand-600 px-5 py-2.5 text-[15px] font-semibold text-white transition hover:bg-brand-700 disabled:cursor-default disabled:bg-slate-100 disabled:text-slate-400">
              {commentBusy ? '...' : 'Post'}
            </button>
          </form>
        </section>
      </div>

      {/* Sticky action footer, per the mock. Draft no longer exists as a
          reachable state for any task created from here on - a task always
          publishes the moment it is created, so this footer only ever needs
          to handle the live statuses. */}
      {mayAct && (
        <footer className="border-t border-slate-200 bg-slate-50/70 px-7 py-4">
          {actionError && (
            <p role="alert" className="mb-2.5 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 ring-1 ring-red-100">
              {actionError}
            </p>
          )}

          {blocking ? (
            <div className="flex items-center gap-2">
              <input
                autoFocus
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                maxLength={500}
                placeholder="What is blocking it?"
                className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
              />
              <button type="button" disabled={busy || !blockReason.trim()}
                onClick={() => void changeStatus('BLOCKED', blockReason.trim())}
                className="rounded-lg bg-red-600 px-3.5 py-2 text-sm font-semibold text-white transition hover:bg-red-700 disabled:opacity-50">
                {busy ? 'Saving...' : 'Mark blocked'}
              </button>
              <button type="button" onClick={() => { setBlocking(false); setBlockReason(''); }}
                className="rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100">
                Back
              </button>
            </div>
          ) : task.status === 'DONE' ? (
            <div className="flex items-center gap-2.5">
              <button type="button" disabled={busy}
                onClick={() => void changeStatus('ONGOING')}
                className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-[15px] font-semibold text-slate-700 transition hover:bg-slate-100 disabled:opacity-50">
                {busy ? 'Saving...' : 'Reopen'}
              </button>
              <span className="text-xs text-slate-400">Moves it back to In progress.</span>
            </div>
          ) : (
            <div className="flex items-center gap-2.5">
              {/* No explicit "Start" step - an assignee moves a PENDING task
                  straight to Done or Blocked on their own accord; ONGOING is
                  still reachable by dragging the card on the My Day board. */}
              <button type="button" disabled={busy}
                onClick={() => void changeStatus('DONE')}
                className="rounded-lg bg-brand-600 px-5 py-2.5 text-[15px] font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:opacity-50">
                {busy ? 'Saving...' : 'Mark complete'}
              </button>
              {task.status === 'BLOCKED' ? (
                <button type="button" disabled={busy}
                  onClick={() => void changeStatus('ONGOING')}
                  className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-[15px] font-semibold text-slate-700 transition hover:bg-slate-100 disabled:opacity-50">
                  Resume
                </button>
              ) : (
                <button type="button" disabled={busy}
                  onClick={() => setBlocking(true)}
                  className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-[15px] font-semibold text-slate-700 transition hover:bg-slate-100 disabled:opacity-50">
                  Block…
                </button>
              )}
              <span className="flex-1" />
              {isCreator && (
                confirmCancel ? (
                  <span className="flex items-center gap-2">
                    <button type="button" disabled={busy}
                      onClick={() => void changeStatus('CANCELLED')}
                      className="rounded-lg bg-red-600 px-3.5 py-2 text-sm font-semibold text-white transition hover:bg-red-700 disabled:opacity-50">
                      Confirm cancel
                    </button>
                    <button type="button" onClick={() => setConfirmCancel(false)}
                      className="rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100">
                      Keep it
                    </button>
                  </span>
                ) : (
                  <button type="button" disabled={busy}
                    onClick={() => setConfirmCancel(true)}
                    className="rounded-lg px-3.5 py-2.5 text-[15px] font-semibold text-slate-400 transition hover:bg-red-50 hover:text-red-600">
                    Put on hold…
                  </button>
                )
              )}
            </div>
          )}

          {task.completionPolicy === 'LEAD_ONLY' && !isCreator && !me?.isLead && (
            <p className="mt-2 text-xs text-slate-400">
              Only the lead (or the creator) can mark this task complete.
            </p>
          )}
        </footer>
      )}
      </>
      )}
    </SidePanel>
  );
}
